import UserRole from './UserRole';
import PersistenceHint from '$shared/constants/PersistenceHint';
import Access from '$shared/constants/Access';
import DateString from '$shared/models/DateString';
import { AuthType } from '$shared/constants/AuthType';
import type { IAuthorization } from '$shared/interfaces/api/IAuthorization';
import type { IUserRole } from '$shared/interfaces/api/IUserRole';
import { Module } from '$shared/constants/Module';

const LocalStorageKey = 'verifier-user';
const CredUserKey = 'verifier-cred-user';
const AuthTypeKey = 'verifier-auth-type';
const CredSelectedOrgKey = 'verifier-cred-org-selected';
const OneTimeTokenUserRole = 'OneTimeUser';

type AuthUserStorage = IAuthorization & { userAuthType: AuthType | null };

export default class Authorization implements IAuthorization {
  public readonly email: string;
  public readonly token: string;
  public readonly tokenId: string;
  public readonly expiresAt: string;
  public role: UserRole;
  public readonly persistenceHint?: PersistenceHint | null;
  public userAuthType: AuthType | null = null;
  public selectedCredOrg: string | null = null;

  constructor(
    email: string,
    token: string,
    tokenId: string,
    role: IUserRole,
    expiresAt: string,
    persistenceHint?: PersistenceHint | null,
    userAuthType?: AuthType | null
  ) {
    this.email = email;
    this.token = token;
    this.tokenId = tokenId;
    this.expiresAt = expiresAt || '';
    this.persistenceHint = persistenceHint || null;
    this.userAuthType = userAuthType || null;
    this.setUserRole(role);
  }

  public get expireDate(): DateString | null {
    return (this.expiresAt && new DateString(this.expiresAt)) || null;
  }

  public get isAuthorised(): boolean {
    return !!this.token;
  }

  public get isCredUser(): boolean {
    return this.userAuthType === AuthType.Cred;
  }

  public setUserAuthType(userAuthType: AuthType | null): void {
    this.userAuthType = userAuthType || null;
  }

  public store(): void {
    const serialisedData: string = JSON.stringify(this.toResponse());
    const storeKey: string = Authorization.getUserInfoStorageKey(this.userAuthType);
    this.storage.setItem(storeKey, serialisedData);
    this.selectedCredOrg && this.storage.setItem(CredSelectedOrgKey, this.selectedCredOrg);
  }

  public clean(): void {
    this.storage.removeItem(LocalStorageKey);
    this.storage.removeItem(CredUserKey);
    this.storage.removeItem(CredSelectedOrgKey);
  }

  public setSelectedOrg(org: string): void {
    if (!org) return;
    this.selectedCredOrg = org;
    this.storage.setItem(CredSelectedOrgKey, org);
  }

  public setCredUserRole(userRole: UserRole | undefined): void {
    if (userRole) {
      this.role = userRole;
    }
    // Store the role to local storage
    this.store();
  }

  private get isSessionBased(): boolean {
    return this.persistenceHint === PersistenceHint.Ephemeral;
  }

  private get storage(): Storage {
    if (this.isSessionBased) {
      return window.sessionStorage;
    }
    return window.localStorage;
  }

  private setUserRole(role: IUserRole): void {
    role && (this.role = new UserRole(role.id, role.name, role.permissions));
  }

  public toResponse(): AuthUserStorage {
    return {
      email: this.email,
      token: this.token,
      tokenId: this.tokenId,
      expiresAt: this.expiresAt || '',
      role: this.role,
      persistenceHint: this.persistenceHint,
      userAuthType: this.userAuthType
    };
  }

  // Support for backward compatability for cred auth type and selected cred org
  // we shall remove this in feature
  static cleanupOldLocalStorage(storage: any): void {
    const authType = window.localStorage.getItem(AuthTypeKey) as AuthType;
    if (!authType) return;

    const data: string | null = Authorization.getUserInfoFromStorage(storage);

    if (data) {
      const storedData: AuthUserStorage = JSON.parse(data);
      if (storedData) {
        storedData.userAuthType = authType;
        const storeKey: string = Authorization.getUserInfoStorageKey(authType);
        storage.setItem(storeKey, JSON.stringify(storedData));
      }
    }

    window.localStorage.removeItem(AuthTypeKey);
    window.localStorage.removeItem(CredSelectedOrgKey);
  }

  // It is for refersh the permissions for cred user because, we have new permission are added
  // In feature we can remove this once all the users are have latest permissions
  static restoreLatestRolePermissionForCredUser(storage: any): void {
    const data: string | null = Authorization.getUserInfoFromStorage(storage);
    if (!data) return;

    const storedData: AuthUserStorage = JSON.parse(data);
    if (storedData.userAuthType !== AuthType.Cred) return;

    const role = storedData.role?.name;
    if (!role) return;

    const storageKey = Authorization.getUserInfoStorageKey(storedData.userAuthType);
    storage.setItem(storageKey, JSON.stringify(storedData));
  }

  static restore(): Authorization | null {
    const storage: Storage = Authorization.getUserInfoFromStorage(window.sessionStorage)
      ? window.sessionStorage
      : window.localStorage;

    Authorization.cleanupOldLocalStorage(storage);
    Authorization.restoreLatestRolePermissionForCredUser(storage);

    const data: string | null = Authorization.getUserInfoFromStorage(storage);

    if (!data) return null;

    const storedData: AuthUserStorage = JSON.parse(data);
    const selectedOrg = storage.getItem(CredSelectedOrgKey);

    const authorization = Authorization.create(storedData, storedData?.userAuthType);
    // set selected org
    authorization.selectedCredOrg = selectedOrg;
    return authorization;
  }

  static create(response: IAuthorization, userAuthType?: AuthType | null): Authorization {
    return new Authorization(
      response?.email,
      response?.token,
      response?.tokenId,
      response?.role,
      response?.expiresAt,
      response?.persistenceHint,
      userAuthType
    );
  }

  static createOneTimeUser(token: string): Authorization {
    const role: UserRole = new UserRole(OneTimeTokenUserRole, OneTimeTokenUserRole, {
      [Module.Integrations]: Access.Write,
      [Module.Organization]: Access.Write,
      [Module.Providers]: Access.Write,
      [Module.ViewSensitiveInfo]: Access.Allowed,
      [Module.ProviderRemoval]: Access.Allowed,
      [Module.SensitiveCvoActions]: Access.Allowed,
      [Module.Enrollments]: Access.Write,
      [Module.ProviderChangeCredentialingStatus]: Access.Allowed
    });
    return new Authorization('', token, '', role, '');
  }

  static isOneTimeUser(): boolean {
    const authData: IAuthorization | null = Authorization.restore();
    return authData?.role?.name === OneTimeTokenUserRole;
  }

  static getUserInfoFromStorage(storage: Storage): string | null {
    return storage.getItem(CredUserKey) || storage.getItem(LocalStorageKey);
  }

  static getUserInfoStorageKey(authType: AuthType | null): string {
    return authType === AuthType.Cred ? CredUserKey : LocalStorageKey;
  }
}
